import posthog from 'posthog-js'

declare global {
  interface Window { dataLayer: unknown[]; gtag?: (...args: unknown[]) => void }
}

let enabled = false

export function initAnalytics(): void {
  if (enabled) return
  const key = import.meta.env.VITE_POSTHOG_KEY
  if (!key || location.hostname === 'localhost') return
  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
    capture_pageview: true,
    persistence: 'localStorage+cookie',
    autocapture: false,
  })
  // Stamp every event with the environment so one PostHog project can separate
  // prod from staging (derived from the hostname, same rule as error reporting).
  const environment = location.hostname.includes('.staging.') ? 'staging' : 'production'
  posthog.register({ environment })
  enabled = true
}

export function track(event: string, props?: Record<string, unknown>): void {
  if (enabled) posthog.capture(event, props)
}

export function identifyUser(userId: string, props?: Record<string, unknown>): void {
  if (enabled) posthog.identify(userId, props)
}

export interface Attribution {
  utm_source?: string; utm_medium?: string; utm_campaign?: string; gclid?: string
}
const ATTR_KEY = 'whl_attribution'

export function captureAttributionFromURL(): void {
  const q = new URLSearchParams(location.search)
  const attr: Attribution = {}
  for (const k of ['utm_source', 'utm_medium', 'utm_campaign'] as const) {
    const v = q.get(k); if (v) attr[k] = v
  }
  const gclid = q.get('gclid'); if (gclid) attr.gclid = gclid
  if (Object.keys(attr).length && !localStorage.getItem(ATTR_KEY)) {
    localStorage.setItem(ATTR_KEY, JSON.stringify(attr)) // first-touch wins
  }
}

export function getAttribution(): Attribution {
  try { return JSON.parse(localStorage.getItem(ATTR_KEY) || '{}') } catch { return {} }
}

// Google Ads — env-gated. Loads gtag.js and fires the signup conversion.
// subscription_active conversion (offline import via stored gclid) is a later step.
export function initGoogleAds(): void {
  const id = import.meta.env.VITE_GADS_ID
  if (!id || location.hostname === 'localhost') return
  const s = document.createElement('script')
  s.async = true
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
  document.head.appendChild(s)
  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() { window.dataLayer.push(arguments) }
  window.gtag('js', new Date())
  window.gtag('config', id)
}

export function trackSignupConversion(): void {
  const id = import.meta.env.VITE_GADS_ID
  // Google generates the conversion LABEL (a random-ish string); send_to must be
  // `AW-XXXX/<label>`. Without the label set, skip — a wrong label silently never
  // registers the conversion.
  const label = import.meta.env.VITE_GADS_SIGNUP_LABEL
  if (id && label && window.gtag) {
    window.gtag('event', 'conversion', { send_to: `${id}/${label}` })
  }
}
